import { firstNonEmptyParam, paramsFromSearchAndHash } from "./urlParams";

export type ConversionPlatform = "google" | "x" | "tiktok" | "meta" | null;
type TikTokConversionEvent = "ClickButton" | "Contact";
type MetaConversionEvent = "Lead" | "Contact";
type MetaConversionParams = {
  contact_us?: "ok";
};

export const leadTrackingKeys = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "ad_source",
  "source",
  "entry_source",
  "gad_source",
  "gad_campaignid",
  "gclid",
  "gbraid",
  "wbraid",
  "twclid",
  "ttclid",
  "fbclid",
];

export const sensitiveUrlQueryKeys = ["email", "email_id"];

const googleSourceValues = new Set(["google", "googlex", "google_ads", "googleads", "adwords", "google-ads"]);
const xSourceValues = new Set(["x", "x_ads", "xads", "twitter", "twitter_ads", "twitter-ads"]);
const tiktokSourceValues = new Set(["tiktok", "tiktok_ads", "tiktokads", "tiktok-ads", "tt", "tt_ads"]);
const metaSourceValues = new Set(["meta", "meta_ads", "metaads", "facebook", "facebook_ads", "facebook-ads", "fb", "fb_ads", "instagram", "instagram_ads", "instagram-ads", "ig", "ig_ads"]);

declare global {
  interface Window {
    gtag_report_conversion?: (url?: string) => false;
    x_report_conversion?: () => void;
    tiktok_report_conversion?: (eventName?: TikTokConversionEvent) => false;
    meta_report_conversion?: (eventName?: MetaConversionEvent, eventParams?: MetaConversionParams) => false;
  }
}

function normalizedParam(params: URLSearchParams, key: string) {
  return firstNonEmptyParam(params, key)?.trim().toLowerCase() || "";
}

function paramsFromSearch(search?: string) {
  return paramsFromSearchAndHash(search);
}

export function resolveConversionPlatform(params: URLSearchParams): ConversionPlatform {
  const sourceValues = [
    normalizedParam(params, "utm_source"),
    normalizedParam(params, "ad_source"),
    normalizedParam(params, "source"),
    normalizedParam(params, "entry_source"),
  ];

  if (sourceValues.some((source) => googleSourceValues.has(source))) return "google";
  if (sourceValues.some((source) => xSourceValues.has(source))) return "x";
  if (sourceValues.some((source) => tiktokSourceValues.has(source))) return "tiktok";
  if (sourceValues.some((source) => metaSourceValues.has(source))) return "meta";

  if (params.has("gclid") || params.has("gbraid") || params.has("wbraid")) return "google";
  if (params.has("twclid")) return "x";
  if (params.has("ttclid")) return "tiktok";
  if (params.has("fbclid")) return "meta";

  return null;
}

export function reportLeadConversion(platform: ConversionPlatform) {
  if (platform === "google") {
    window.gtag_report_conversion?.();
    return;
  }

  if (platform === "x") {
    window.x_report_conversion?.();
    return;
  }

  if (platform === "tiktok") {
    window.tiktok_report_conversion?.("Contact");
    return;
  }

  if (platform === "meta") {
    window.meta_report_conversion?.("Lead");
    window.meta_report_conversion?.("Contact", { contact_us: "ok" });
  }
}

export function reportLeadEntryConversion(search?: string) {
  if (resolveConversionPlatform(paramsFromSearch(search)) !== "tiktok") return;
  window.tiktok_report_conversion?.("ClickButton");
}
